import PropTypes from 'prop-types';
import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PHYSICAL_DIRECTIONS } from 'src/utils/physical-api';
import HtsAutocomplete from '../hts/hts-autocomplete';

export const isItemizedDirection = (direction) => direction === PHYSICAL_DIRECTIONS.GE_USA;

export const ORDER_CATEGORIES = [
  'Household goods',
  'Clothing',
  'Phone',
  'Computer',
  'Jewelry',
  'Toys',
  'Auto/Moto parts',
  'Food supplements',
  'Audio/Video equipment',
  'Cosmetics',
  'Parfumes',
  'Glass products',
  'Printed products',
  'Lamps',
  'Glasses (Sunglasses)',
  'Footwear',
  'Bags',
  'Sports Equipment',
  'Tools',
  'Various consumer products',
  'Computer parts',
  'Music Instruments',
  'Painting',
];

let itemKey = 0;
const nextKey = () => {
  itemKey += 1;
  return itemKey;
};

export const newItem = () => ({
  key: nextKey(),
  hts: null,
  category: ORDER_CATEGORIES[0],
  quantity: 1,
  unit_price_usd: null,
});

export const itemsFromOrder = (physicalItems = []) =>
  physicalItems.map((item) => ({
    key: nextKey(),
    hts: item.hts,
    category: item.category,
    quantity: item.quantity,
    unit_price_usd: item.unit_price_usd,
  }));

export const itemsTotal = (items = []) =>
  Math.round(
    items.reduce(
      (sum, item) => sum + (item.quantity || 0) * (Number(item.unit_price_usd) || 0),
      0
    ) * 100
  ) / 100;

export const itemsComplete = (items = []) =>
  items.length > 0 &&
  items.every(
    (item) =>
      item.hts &&
      item.category &&
      item.quantity >= 1 &&
      item.unit_price_usd !== null &&
      item.unit_price_usd !== '' &&
      Number(item.unit_price_usd) >= 0
  );

export const itemsPayload = (items = []) =>
  items.map((item) => ({
    hts_id: item.hts.id,
    category: item.category,
    quantity: item.quantity,
    unit_price_usd: Number(item.unit_price_usd),
  }));

const ParcelItemsEditor = ({ items, onChange, disabled }) => {
  const update = (key, patch) =>
    onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Items
      </Typography>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 1.5,
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Box sx={{ gridColumn: 'span 4' }}>
            <HtsAutocomplete
              value={item.hts?.code ?? null}
              disabled={disabled}
              onChange={(hts) => update(item.key, { hts })}
            />
          </Box>
          <Box sx={{ gridColumn: 'span 3' }}>
            <Autocomplete
              options={ORDER_CATEGORIES}
              value={item.category}
              disabled={disabled}
              onChange={(e, category) => update(item.key, { category })}
              renderInput={(params) => <TextField {...params} label="Category" />}
            />
          </Box>
          <Box sx={{ gridColumn: 'span 1' }}>
            <TextField
              type="number"
              label="Qty"
              inputProps={{ min: 1, step: 1 }}
              value={item.quantity}
              disabled={disabled}
              onChange={(e) =>
                update(item.key, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })
              }
              fullWidth
            />
          </Box>
          <Box sx={{ gridColumn: 'span 2' }}>
            <TextField
              type="number"
              label="Price per item ($)"
              inputProps={{ min: 0, step: '0.01' }}
              value={item.unit_price_usd ?? ''}
              disabled={disabled}
              onChange={(e) =>
                update(item.key, { unit_price_usd: e.target.value === '' ? null : e.target.value })
              }
              fullWidth
            />
          </Box>
          <Box sx={{ gridColumn: 'span 1', textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              ${itemsTotal([item]).toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: 'span 1', textAlign: 'right' }}>
            <IconButton
              color="error"
              onClick={() => onChange(items.filter((other) => other.key !== item.key))}
              disabled={disabled || items.length <= 1}
              aria-label="Remove item"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={() => onChange([...items, newItem()])}
      >
        Add item
      </Button>
    </Box>
  );
};

ParcelItemsEditor.propTypes = {
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ParcelItemsEditor.defaultProps = {
  disabled: false,
};

export default ParcelItemsEditor;
